import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { firestoreService } from '../services/firestoreService';

export const adminLogin = async (credentials) => {
  try {
    const { email, password } = credentials;
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    
    // Get the ID token
    const tokenResult = await userCredential.user.getIdTokenResult();
    const token = tokenResult.token; 
    
    // Store token in localStorage for persistence
    localStorage.setItem('adminToken', token);
    
    return {
      claims: tokenResult.claims,
      user: tokenResult.user,
      token,
      message: 'Login successful'
    };
  } catch (error) {
    console.error('Error during admin login:', error);
    throw error;
  }
};

export const verifyAdminToken = async (token) => {
  try {
    // Check if there's a current Firebase user
    const user = auth.currentUser;
    
    if (!user) {
      throw new Error('No authenticated user');
    }
    
    // Get fresh token and claims
    const tokenResult = await user.getIdTokenResult(true);
    const freshToken = tokenResult.token;
    
    // Verify admin claim
    if (!tokenResult.claims.admin) {
      throw new Error('User does not have admin privileges');
    }
    
    // Update token in localStorage
    localStorage.setItem('adminToken', freshToken);
    
    return {
      valid: true,
      user,
      claims: tokenResult.claims,
      token: freshToken
    };
  } catch (error) {
    console.error('Error verifying admin token:', error);
    localStorage.removeItem('adminToken');
    throw error;
  }
};

export const fetchScheduleData = async (month = null, year = null) => {
  try {
    // Verify user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }
    
    let filters = [];
    
    // Add date filters if provided
    if (month !== null && year !== null) {
      // Create start and end dates for the month
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0, 23, 59, 59);
      
      filters = [
        { field: 'createdAt', operator: '>=', value: startDate },
        { field: 'createdAt', operator: '<=', value: endDate }
      ];
    }
    
    // Fetch schedules - this is ONE read operation
    const schedules = await firestoreService.getCollection('donationSchedules', filters);
    
    // If customer data is already embedded in schedules, return immediately (0 additional reads)
    if (schedules.length === 0 || schedules[0].firstName) {
      return schedules;
    }
    
    // Otherwise, fetch customer data in batch (1 additional read per unique customer)
    // Get unique customer IDs
    const uniqueCustomerIds = [...new Set(schedules.map(s => s.customerId).filter(Boolean))];
    
    // Fetch all customer data in parallel (batch operation)
    const customerDataPromises = uniqueCustomerIds.map(customerId => 
      firestoreService.getDocument('donors', customerId).catch(err => {
        console.warn(`Failed to fetch customer ${customerId}:`, err);
        return null; // Return null for failed fetches
      })
    );
    
    const customersData = await Promise.all(customerDataPromises);
    
    // Create a map for quick lookup
    const customerMap = {};
    customersData.forEach((customer, index) => {
      if (customer) {
        customerMap[uniqueCustomerIds[index]] = customer;
      }
    });
    
    // Merge customer data with schedules
    return schedules.map(schedule => {
      const customer = customerMap[schedule.customerId];
      // Destructure to exclude 'id' field from customer data to prevent overwriting schedule.id
      const { id: customerId, ...customerDataWithoutId } = customer || {};
      
      return {
        ...schedule,
        firstName: customer?.firstName || 'Unknown',
        lastName: customer?.lastName || '',
        email: customer?.email || '',
        ...customerDataWithoutId // Merge all customer fields except 'id'
      };
    });
  } catch (error) {
    console.error('Error fetching schedule data:', error);
    throw error;
  }
};

export const adminLogout = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem('adminToken');
    return { message: 'Logout successful' };
  } catch (error) {
    console.error('Error during admin logout:', error);
    throw error;
  }
};;

export const fetchAdminStats = async () => {
  try {
    // Verify user is authenticated
    const user = auth.currentUser;
    if (!user) {
      throw new Error('Not authenticated');
    }
    
    // Get statistics from multiple Firestore collections
    const [donations, schedules, campaigns, funds] = await Promise.all([
      firestoreService.getCollection('payments'),
      firestoreService.getCollection('donationSchedules'),
      firestoreService.getCollection('campaigns'),
      firestoreService.getCollection('funds')
    ]);
    
    // Calculate basic statistics
    const stats = {
      totalDonations: donations.length,
      totalSchedules: schedules.length,
      activeCampaigns: campaigns.filter(c => c.active).length,
      activeFunds: funds.filter(f => f.active).length,
      totalAmount: donations.reduce((sum, donation) => sum + (donation.amount || 0), 0),
      activeSchedules: schedules.filter(s => s.status === 'active').length
    };
    
    return stats;
  } catch (error) {
    console.error('Error fetching admin stats:', error);
    throw error;
  }
};
